import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Plus, BookOpen } from 'lucide-react'
import AppShell from '../components/AppShell.jsx'
import Card from '../components/ui/Card.jsx'
import Badge from '../components/ui/Badge.jsx'
import Button from '../components/ui/Button.jsx'
import Modal from '../components/ui/Modal.jsx'
import Input, { Field } from '../components/ui/Input.jsx'
import { SkeletonCard } from '../components/ui/Skeleton.jsx'
import { useToast } from '../components/ui/Toast.jsx'
import { getClient, listProjectsForClient, createProject } from '../lib/api.js'

export default function ClientDetail() {
  const { clientId } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const [client, setClient] = useState(null)
  const [projects, setProjects] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [busy, setBusy] = useState(false)

  async function refresh() {
    const [c, p] = await Promise.all([getClient(clientId), listProjectsForClient(clientId)])
    setClient(c)
    setProjects(p)
  }
  useEffect(() => { refresh() }, [clientId])

  async function handleCreate(e) {
    e.preventDefault()
    setBusy(true)
    try {
      const project = await createProject(clientId, title.trim() || 'Untitled flipbook')
      toast('Project created', 'success')
      navigate(`/editor/${project.id}`)
    } catch (err) {
      toast(err.message, 'error')
      setBusy(false)
    }
  }

  return (
    <AppShell>
      <Link to="/clients" className="inline-flex items-center gap-1.5 text-muted hover:text-ink text-[13px] mb-4 transition-colors">
        <ArrowLeft size={14} /> All clients
      </Link>

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display font-bold text-[24px]">{client?.name || '…'}</h1>
          {client?.contact_email && <p className="text-muted text-[14px] mt-1">{client.contact_email}</p>}
        </div>
        <Button icon={Plus} onClick={() => setModalOpen(true)}>New flipbook</Button>
      </div>

      {projects === null ? (
        <div className="grid grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <SkeletonCard key={i} />)}
        </div>
      ) : projects.length === 0 ? (
        <Card className="p-10 text-center">
          <BookOpen size={32} className="mx-auto mb-3 text-muted-2" />
          <p className="text-muted text-[14px] mb-4">No flipbooks for {client?.name} yet.</p>
          <Button icon={Plus} className="mx-auto" onClick={() => setModalOpen(true)}>New flipbook</Button>
        </Card>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          {projects.map((p) => (
            <Link key={p.id} to={`/editor/${p.id}`}>
              <Card hover className="p-4 cursor-pointer h-full">
                <div className="aspect-[4/3] rounded-md bg-panel2 mb-3 flex items-center justify-center text-muted-2">
                  <BookOpen size={28} />
                </div>
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold text-[14px] truncate">{p.title}</div>
                  <Badge tone={p.status === 'published' ? 'published' : 'draft'}>{p.status}</Badge>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="New flipbook">
        <form onSubmit={handleCreate}>
          <Field label="Title" hint="You can change this anytime once you've dropped a PDF.">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. 2026 Product Catalogue" autoFocus />
          </Field>
          <Button type="submit" className="w-full mt-2" loading={busy}>Create & open editor</Button>
        </form>
      </Modal>
    </AppShell>
  )
}
